// Vault-owned onboarding orchestrator. The DDC gateway's
// /auth/onboarding/status endpoint returns a `next_user_action`
// discriminator that tells the client exactly which step is owed
// next. We switch on it explicitly here so the call site reads as a
// state machine, not as "trust whatever the SDK does internally".
//
// The SDK's `runOnboarding` is still available — but it drives the
// flow from the boolean signals (faucet_eligible / gateway_is_proxy)
// and emits its own events. With the server now telling us the next
// action directly, we treat the SDK as a transport (POST helpers,
// signer shape) and own the choreography ourselves.
//
// Trust model: `next_user_action` is a server-controlled string. We
// allow-list the known values; an unknown value throws so a future
// server change can't silently push the UI into a no-op state.

// ddc-s3-gateway#16 reshuffled the action names without changing the
// underlying wire effects:
//   - `bootstrap` split into `fund_wallet` (native faucet drop) and
//     `seed_ddc_deposit` (DDC deposit seed). Both still resolve via
//     POST /auth/onboarding/bootstrap.
//   - `none` was renamed to `ready` for the terminal state.
//   - `register_credential` is a new intermediate state. The credential
//     itself is registered inline by vault-api ClaimVault (POST
//     /auth/credentials with a delegation token) during
//     `sdk.vault.ensure()` — vault can't register it out-of-band
//     because `buildVaultDelegationToken` is not exported from the
//     SDK. But `register_credential` outranks `add_proxy` in the
//     gateway's precedence, so when *both* are needed the status
//     masks add_proxy. Claim then fails on CreateBucket with "ensure
//     the gateway address is registered as a proxy". To recover, we
//     pivot on `gateway_is_proxy`: if false we run addProxy here even
//     though the action label says `register_credential`, then exit
//     so claim can register the credential + create the bucket with
//     the proxy already in place.
// Accepting both old and new spellings keeps the orchestrator
// compatible with whichever gateway vintage is reachable.
export type OnboardingNextAction =
  | 'bootstrap'
  | 'fund_wallet'
  | 'seed_ddc_deposit'
  | 'register_credential'
  | 'add_proxy'
  | 'none'
  | 'ready';

const KNOWN_ACTIONS: ReadonlySet<OnboardingNextAction> = new Set<OnboardingNextAction>([
  'bootstrap',
  'fund_wallet',
  'seed_ddc_deposit',
  'register_credential',
  'add_proxy',
  'none',
  'ready',
]);

function isKnownAction(value: string): value is OnboardingNextAction {
  return (KNOWN_ACTIONS as ReadonlySet<string>).has(value);
}

/**
 * Status shape returned by GET /auth/onboarding/status. Mirrors the
 * SDK's internal `OnboardingStatus` plus the `next_user_action`
 * discriminator. We don't import the SDK type here — it lives behind
 * a deep internal path that isn't in the SDK's exports map, and
 * inlining keeps the wire contract owned by this module.
 */
export interface OnboardingStatusWithAction {
  address: string;
  native_balance: string;
  native_floor: string;
  ddc_deposit_total: string;
  gateway_is_proxy: boolean;
  faucet_eligible: boolean;
  ddc_seed_eligible: boolean;
  next_user_action: string;
}

// Mirrors the relevant subset of vault-sdk EnsureProgressEvent kinds
// so the existing VaultLayout/VaultConnectFlow UI (which switches on
// `kind`) keeps working when we replace the SDK's onboarding side
// with this orchestrator.
export type OnboardingFlowEvent =
  | { kind: 'funding-wallet' }
  | { kind: 'gateway-authorization-required'; status: OnboardingStatusWithAction }
  | { kind: 'authorizing-gateway' }
  | { kind: 'authorization-submitted'; txHash: string }
  | { kind: 'awaiting-chain-confirmation' }
  | { kind: 'ready' };

export interface RunOnboardingFlowDeps {
  /** Latest status from GET /auth/onboarding/status. Must include next_user_action. */
  getStatus: () => Promise<OnboardingStatusWithAction>;
  /** POST /auth/onboarding/bootstrap. */
  bootstrap: () => Promise<void>;
  /** Submit proxy.addProxy(gateway) via the user's wallet. Returns the tx hash. */
  addProxy: () => Promise<{ txHash: string }>;
  onProgress?: (event: OnboardingFlowEvent) => void;
  /**
   * Safety bound. A misbehaving server that keeps returning the same
   * pre-terminal action would otherwise hang the UI. The flow submits
   * addProxy at most once (it returns straight after), so the worst legal
   * path is fund_wallet → seed_ddc_deposit → register_credential/add_proxy
   * (= return) ≈ 3 iterations; 5 leaves slack for transient bootstrap
   * status repeats while the faucet/DDC seed settles.
   */
  maxIterations?: number;
}

const DEFAULT_MAX_ITERATIONS = 5;

export async function runOnboardingFlow(deps: RunOnboardingFlowDeps): Promise<void> {
  const max = deps.maxIterations ?? DEFAULT_MAX_ITERATIONS;
  for (let i = 0; i < max; i++) {
    const status = await deps.getStatus();
    if (!isKnownAction(status.next_user_action)) {
      throw new Error(`onboarding: unknown next_user_action "${status.next_user_action}"`);
    }
    const action: OnboardingNextAction = status.next_user_action;
    switch (action) {
      case 'none':
      case 'ready':
        deps.onProgress?.({ kind: 'ready' });
        return;
      case 'register_credential':
        // Claim registers the credential inline. Our only remaining
        // worry is the proxy — `register_credential` outranks
        // `add_proxy` in the gateway state machine, so when both are
        // needed the status hides the add_proxy step. Pivot on the
        // raw `gateway_is_proxy` flag to recover it.
        if (status.gateway_is_proxy) {
          deps.onProgress?.({ kind: 'ready' });
          return;
        }
        deps.onProgress?.({ kind: 'gateway-authorization-required', status });
        deps.onProgress?.({ kind: 'authorizing-gateway' });
        {
          const { txHash } = await deps.addProxy();
          deps.onProgress?.({ kind: 'authorization-submitted', txHash });
          deps.onProgress?.({ kind: 'awaiting-chain-confirmation' });
        }
        // addProxy resolves only after the extrinsic is in a block, so the
        // proxy is live now. Don't loop back to re-read status: add_proxy is
        // advisory and the gateway's proxy check can briefly still report
        // false (indexer lag), which would re-prompt the user to sign a
        // duplicate addProxy. Hand back to claim, which registers the
        // credential + creates the bucket with the proxy already in place.
        deps.onProgress?.({ kind: 'ready' });
        return;
      case 'bootstrap':
      case 'fund_wallet':
      case 'seed_ddc_deposit':
        deps.onProgress?.({ kind: 'funding-wallet' });
        await deps.bootstrap();
        continue;
      case 'add_proxy': {
        deps.onProgress?.({ kind: 'gateway-authorization-required', status });
        deps.onProgress?.({ kind: 'authorizing-gateway' });
        const { txHash } = await deps.addProxy();
        deps.onProgress?.({ kind: 'authorization-submitted', txHash });
        deps.onProgress?.({ kind: 'awaiting-chain-confirmation' });
        // Proxy is live once addProxy resolves (in-block). Same reasoning as
        // the register_credential branch: returning avoids a duplicate
        // addProxy prompt on indexer lag.
        deps.onProgress?.({ kind: 'ready' });
        return;
      }
    }
  }
  throw new Error(`onboarding: did not terminate within ${max} iterations`);
}
